import { cn } from '@ordo/ui/cn'
import { ArrowRight, Check } from 'lucide-react'
import { useMemo } from 'react'

import { useSubstitutionRequestsQuery } from '@/api/substitution-requests'
import { Badge } from '@/components/chefs/badge'
import { useActiveKitchen } from '@/hooks/chefs/useActiveKitchen'
import { useSubstitutionModal } from '@/hooks/chefs/useSubstitutionModal'
import { toUiSubstitutions } from '@/lib/chefs/substitution-adapters'
import { findDisplayState } from '@/lib/chefs/substitution-lookup'

interface Props {
  ingredientName: string
  ingredientUnit: string
  referenceDate?: Date
  className?: string
}

export function IngredientSubstitutionDisplay({ ingredientName, ingredientUnit, referenceDate, className }: Props) {
  const kitchenLocationId = useActiveKitchen()
  const subsQuery = useSubstitutionRequestsQuery(
    { kitchenLocationId: kitchenLocationId ?? '' },
    { enabled: Boolean(kitchenLocationId) },
  )
  const openModal = useSubstitutionModal((s) => s.openModal)

  const scenarioSubs = useMemo(() => toUiSubstitutions(subsQuery.data?.requests ?? []), [subsQuery.data])
  const date = referenceDate ?? new Date()
  const state = findDisplayState(scenarioSubs, ingredientName, ingredientUnit, date)

  if (state.status === 'approved') {
    return (
      <span className={cn('flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
        <span className="text-muted-foreground line-through">{ingredientName}</span>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
        <span className="font-medium text-foreground">{state.substitution.replacement}</span>
        <Badge className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] text-green-800">
          <Check className="-ml-0.5 mr-0.5 h-3 w-3" strokeWidth={2.5} /> Substitution approved
        </Badge>
      </span>
    )
  }

  if (state.status === 'pending') {
    return (
      <span className={cn('flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
        <span className="text-foreground">{ingredientName}</span>
        <button
          type="button"
          onClick={() => openModal({ view: 'ledger' })}
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100">
          Substitution requested
        </button>
      </span>
    )
  }

  return <span className={cn('text-foreground', className)}>{ingredientName}</span>
}
